import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from "@angular/core";
import { IButtonDirective, IIconComponent, ModalService, ToasterService } from "@front/m19-ui";
import { DspAudienceListComponent } from "@m19-board/dsp/dsp-audiences-selector/dsp-audience-list/dsp-audience-list.component";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { Marketplaces } from "@front/m19-models";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { map, switchMap } from "rxjs/operators";
import { AccountSelectionService, AuthService, DspStrategyService } from "@front/m19-services";
import {
  CampaignType,
  StrategyDspAudience,
  StrategyDspAudiencePreset,
  StrategyDspAudienceType,
  StrategyDspFunnel,
} from "@front/m19-api-client";
import {
  DspAudienceModalComponent,
  DspAudienceModalData,
  DspAudienceModalResult,
} from "@m19-board/dsp/dsp-audience-modal/dsp-audience-modal.component";
import { mergeWith, startWith, Subject } from "rxjs";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { DspAudiencePresetSelectorComponent } from "@m19-board/dsp/dsp-audience-preset-selector/dsp-audience-preset-selector.component";
import { DspSmartAudienceSelectorComponent } from "@m19-board/dsp/dsp-smart-audience-selector/dsp-smart-audience-selector.component";
import { FormControl } from "@angular/forms";
import { DspSmartAudiencesService } from "@m19-board/dsp/dsp-smart-audiences-service/dsp-smart-audiences.service";

@Component({
  selector: "app-strategy-page-dsp-audiences",
  imports: [
    IButtonDirective,
    IIconComponent,
    DspAudienceListComponent,
    TranslocoDirective,
    DspAudiencePresetSelectorComponent,
    DspSmartAudienceSelectorComponent,
  ],
  templateUrl: "./strategy-page-dsp-audiences.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageDspAudiencesComponent {
  private readonly strategyPageService = inject<StrategyPageService<CampaignType.DSP>>(StrategyPageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly dspStrategiesService = inject(DspStrategyService);
  private readonly dspSmartAudiencesService = inject(DspSmartAudiencesService);
  private readonly authService = inject(AuthService);
  private readonly toasterService = inject(ToasterService);
  private readonly translationService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);

  protected readonly CampaignType = CampaignType;
  protected readonly StrategyDspAudienceType = StrategyDspAudienceType;
  protected readonly StrategyDspAudiencePreset = StrategyDspAudiencePreset;

  strategy = this.strategyPageService.strategy;
  strategy$ = toObservable(this.strategy);

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, {
    initialValue: false,
  });

  readonly allowPreset = computed(() => this.strategy()?.dspFunnel !== StrategyDspFunnel.AWARENESS);

  readonly isUpdatingAudiences = signal(false);

  readonly audiencePresetControl = new FormControl<StrategyDspAudiencePreset>(StrategyDspAudiencePreset.MANUAL, {
    nonNullable: true,
  });

  readonly marketplaceCurrency = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      map((am) => Marketplaces[am.marketplace].currency),
    ),
  );

  readonly locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)));

  readonly updatedAudienceWhitelistSubject = new Subject<StrategyDspAudience[]>();
  readonly updatedAudienceBlacklistSubject = new Subject<StrategyDspAudience[]>();
  readonly strategyAudienceWhitelist$ = this.strategy$.pipe(
    map((strategy) => strategy?.dspAudiences ?? []),
    mergeWith(this.updatedAudienceWhitelistSubject),
    map((strategyAudiences) => {
      return strategyAudiences.reduce((acc: StrategyDspAudience[][], audience) => {
        if (audience.type !== StrategyDspAudienceType.WHITELIST) {
          return acc;
        }
        if (!acc[audience.group]) {
          acc[audience.group] = [];
        }
        acc[audience.group].push(audience);
        return acc;
      }, []);
    }),
    startWith([]),
  );

  readonly strategyAudienceBlacklist$ = this.strategy$.pipe(
    map((strategy) => strategy?.dspAudiences ?? []),
    mergeWith(this.updatedAudienceBlacklistSubject),
    map((strategyAudiences) => {
      return [strategyAudiences.filter((audience) => audience.type === StrategyDspAudienceType.BLACKLIST)];
    }),
    startWith([]),
  );

  readonly whitelistedAudiences = toSignal(this.strategyAudienceWhitelist$, { requireSync: true });
  readonly blacklistedAudiences = toSignal(this.strategyAudienceBlacklist$, { requireSync: true });

  readonly hasBlacklistedAudiences = computed(
    () => this.blacklistedAudiences().length && this.blacklistedAudiences()[0].length,
  );

  constructor() {
    // TODO(joseph): replace by signals forms once available
    effect(() => {
      if (!this.allowPreset()) {
        this.audiencePresetControl.setValue(StrategyDspAudiencePreset.MANUAL);
      } else if (this.strategy()?.audiencePreset) {
        this.audiencePresetControl.setValue(this.strategy()!.audiencePreset!);
      }
    });
  }

  editAudiences(audienceType: StrategyDspAudienceType) {
    if (this.isReadOnly()) return;
    this.modalService
      .openModal<DspAudienceModalData, DspAudienceModalResult>(DspAudienceModalComponent, {
        size: "xl",
        height: "fill",
        data: {
          audienceType,
          selectedAudiences:
            audienceType === StrategyDspAudienceType.BLACKLIST
              ? this.blacklistedAudiences()
              : this.whitelistedAudiences(),
        },
        modalTitle:
          audienceType === StrategyDspAudienceType.BLACKLIST
            ? this.translationService.translate("dsp-strategy-page.edit_audience_blacklist")
            : this.translationService.translate("dsp-strategy-page.edit_audiences"),
        padding: "horizontal",
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(({ addedAudiences, deletedAudiences }) => {
          return this.dspStrategiesService.updateStrategyDspAudiences(
            this.strategy()!.strategyId!,
            addedAudiences,
            deletedAudiences,
            audienceType,
          );
        }),
      )
      .subscribe({
        next: (strategyAudiences) => {
          if (audienceType === StrategyDspAudienceType.BLACKLIST) {
            this.toasterService.success(
              this.translationService.translate("dsp-strategy-page.audience_blacklist_updated_successfully"),
            );
            this.updatedAudienceBlacklistSubject.next(strategyAudiences);
          } else {
            this.toasterService.success(
              this.translationService.translate("dsp-strategy-page.update_audiences_success"),
            );
            this.updatedAudienceWhitelistSubject.next(strategyAudiences);
          }
        },
        error: () => {
          this.toasterService.error(this.translationService.translate("dsp-strategy-page.update_audiences_error"));
        },
      });
  }

  saveSmartAudienceSelection() {
    this.isUpdatingAudiences.set(true);
    this.dspSmartAudiencesService
      .createAudiencesFromSmartAudiencePreset(
        this.strategy()!.name!,
        this.strategy()!.asins.map((a) => a.asin),
        this.audiencePresetControl.value,
      )
      .pipe(
        switchMap((audiences) => {
          return this.dspStrategiesService.replaceStrategyDspAudiences(
            this.strategy()!,
            audiences.whitelistAudiences,
            audiences.blacklistAudiences,
          );
        }),
        switchMap(() =>
          this.dspStrategiesService.updateDspStrategyAudiencePreset(
            this.strategy()!.strategyId!,
            this.audiencePresetControl.value,
          ),
        ),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(this.translationService.translate("dsp-strategy-page.update_audiences_success"));
          this.isUpdatingAudiences.set(false);
        },
        error: () => {
          this.toasterService.error(this.translationService.translate("dsp-strategy-page.update_audiences_error"));
          this.isUpdatingAudiences.set(false);
        },
      });
  }
}
